import {Button, CrossIcon, Input, Select} from '@kit';

import React, {useEffect, useState} from 'react';

import {useUser} from '../../../components/use-user';
import {getApolloError} from '../../../components/utils';
import {MapLabelFragment, useDeleteMapLabelMutation, useUpdateMapLabelMutation} from '../../../generated/types';
import {Link} from '../../../lib/routes';
import {DEPTH_TITLES, KIND_TITLES, MARKER_TITLES, isDepth, isMarkerKind} from './map-symbols';

const POINT_KINDS = ['shop', 'guild', 'tavern', 'villa', 'temple', 'civic', 'misc', 'spot', 'statue'] as const;

import css from './map-view.module.css';

type Props = {
	label: MapLabelFragment;
	onClose: () => void;
	/** сразу открыть форму правки: так открывается только что добавленная метка */
	startEditing?: boolean;
	onDeleted?: () => void;
};

const kindTitle = (label: MapLabelFragment) =>
	isMarkerKind(label.marker)
		? MARKER_TITLES[label.marker]
		: isDepth(label.marker)
			? `${KIND_TITLES[label.kind] || 'Подземье'} · ${DEPTH_TITLES[label.marker]}`
			: KIND_TITLES[label.kind] || '';

export const MapLabelCard: React.FC<Props> = ({label, onClose, startEditing = false, onDeleted}) => {
	const user = useUser();
	const [editing, setEditing] = useState(startEditing);
	const [title, setTitle] = useState(label.title);
	const [originalTitle, setOriginalTitle] = useState(label.originalTitle);
	const [wikiArticleId, setWikiArticleId] = useState(label.wikiArticleId || '');
	const [marker, setMarker] = useState(label.marker || '');
	const [update, {loading, error}] = useUpdateMapLabelMutation();
	const [remove, {loading: removing, error: removeError}] = useDeleteMapLabelMutation({refetchQueries: ['GameMapData']});

	useEffect(() => {
		setEditing(startEditing);
		setMarker(label.marker || '');
		setTitle(label.title);
		setOriginalTitle(label.originalTitle);
		setWikiArticleId(label.wikiArticleId || '');
	}, [label, startEditing]);

	const markerItems = [{value: '', label: 'Без значка'}, ...POINT_KINDS.map((kind) => ({value: kind, label: MARKER_TITLES[kind]}))];
	if (marker && !markerItems.some((item) => item.value === marker)) markerItems.push({value: marker, label: isMarkerKind(marker) ? MARKER_TITLES[marker] : marker});

	const save = async () => {
		const result = await update({
			variables: {id: label.id, label: {title, originalTitle, wikiArticleId: wikiArticleId.trim(), marker}},
		});
		if (result.data) setEditing(false);
	};

	return (
		<section className={css.card} aria-label={label.title || 'Место на карте'}>
			<div className={css.cardHeader}>
				<div>
					<h2 className={css.cardTitle}>{label.title || 'Без названия'}</h2>
					{label.originalTitle && <div className={css.cardOriginal}>{label.originalTitle}</div>}
				</div>
				<Button appearance="subtle" icon={<CrossIcon />} aria-label="Закрыть" onClick={onClose} />
			</div>
			<div className={css.cardKind}>{kindTitle(label)}</div>
			{label.wikiArticleId && !editing && (
				<Link route="wikiArticle" params={{id: label.wikiArticleId}} className={css.cardLink}>
					Статья в вики
				</Link>
			)}
			{user?.isAdmin &&
				(editing ? (
					<div className={css.cardForm}>
						<Input label="Название" value={title} onChange={setTitle} appearance="outline" />
						<Input label="Оригинал" value={originalTitle} onChange={setOriginalTitle} appearance="outline" />
						<Input
							label="Статья вики"
							helperText="Адрес статьи: waterdeep"
							value={wikiArticleId}
							onChange={setWikiArticleId}
							appearance="outline"
						/>
						<Select label="Значок" items={markerItems} value={marker} onChange={(values) => setMarker(String(values[0] ?? ''))} disableClear />
						{error && <div className={css.error}>{getApolloError(error).message}</div>}
						{removeError && <div className={css.error}>{getApolloError(removeError).message}</div>}
						<div className={css.cardButtons}>
							<Button appearance="primary" loading={loading} onClick={save}>
								Сохранить
							</Button>
							<Button onClick={() => setEditing(false)}>Отмена</Button>
							<Button
								appearance="subtle"
								loading={removing}
								onClick={async () => {
									if (!window.confirm(`Удалить метку «${label.title}»?`)) return;
									const result = await remove({variables: {id: label.id}});
									if (result.data) onDeleted?.();
								}}
							>
								Удалить
							</Button>
						</div>
					</div>
				) : (
					<Button appearance="subtle" onClick={() => setEditing(true)}>
						Изменить
					</Button>
				))}
		</section>
	);
};
